import type { FC, FormEvent, ReactNode } from 'react'
import type { ContactErrors, ContactField, ContactResponse, ContactValues } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { Check, CircleAlert, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { EMPTY_CONTACT, hasErrors, normalizeContact, validateContact } from '@/lib/contact'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { HoverArrow } from '@/components/landing/hover-arrow'
import { ContactProgress } from '@/components/landing/contact-progress'

import { COMPANY_SIZES, CONTACT_LIMITS, CONTACT_STEPS, CONTACT_TOPICS } from '@/constants/contact'

type Status = 'idle' | 'submitting' | 'success'

type Direction = 'none' | 'forward' | 'back'

interface FieldRowProps {
    field: ContactField
    label: string
    error?: string
    optional?: boolean
    children: ReactNode
}

const CONTROL_CLASS = 'h-12 rounded-md border-input bg-background px-4 text-base text-foreground shadow-[0_1px_2px_rgba(10,37,64,0.05)] placeholder:text-muted-foreground/70 md:text-base focus-visible:border-primary focus-visible:ring-[3px] focus-visible:ring-primary/15 aria-invalid:border-destructive aria-invalid:ring-[3px] aria-invalid:ring-destructive/10 aria-invalid:focus-visible:border-destructive aria-invalid:focus-visible:ring-destructive/15'

const HEADING_CLASS = 'font-heading text-[26px] leading-[1.2] font-[450] tracking-[-0.015em] text-foreground sm:text-[28px]'

const FALLBACK_ERROR = "We couldn't send your message right now. Please try again in a moment."

const fieldId = (field: ContactField): string => `contact-${field}`

const errorId = (field: ContactField): string => `contact-${field}-error`

const replaceErrors = (previous: ContactErrors, fields: Array<ContactField>, next: ContactErrors): ContactErrors => {
    const merged: ContactErrors = { ...previous }

    for (const field of fields) {
        if (next[field]) merged[field] = next[field]
        else delete merged[field]
    }

    return merged
}

const firstStepWithError = (fieldErrors: ContactErrors): number =>
    CONTACT_STEPS.findIndex(item => item.fields.some(field => fieldErrors[field]))

const FieldRow: FC<FieldRowProps> = ({
    field,
    label,
    error,
    optional = false,
    children
}): ReactNode => {
    return (
        <div className='grid gap-2 sm:grid-cols-[168px_minmax(0,1fr)] sm:gap-x-6'>
            <Label
                htmlFor={fieldId(field)}
                className='flex-wrap content-center gap-x-2 gap-y-0.5 text-[15px] leading-snug text-foreground sm:h-12'
            >
                {label}

                {optional && (
                    <span className='text-[13px] font-normal text-muted-foreground'>
                        Optional
                    </span>
                )}
            </Label>

            <div className='min-w-0'>
                {children}

                {error && (
                    <p id={errorId(field)} className='mt-2 flex items-start gap-1.5 text-sm leading-snug text-destructive'>
                        <CircleAlert className='mt-px size-4 shrink-0' aria-hidden='true' />
                        {error}
                    </p>
                )}
            </div>
        </div>
    )
}

export const ContactForm: FC = (): ReactNode => {
    const [step, setStep] = useState(0)
    const [direction, setDirection] = useState<Direction>('none')
    const [values, setValues] = useState<ContactValues>(EMPTY_CONTACT)
    const [errors, setErrors] = useState<ContactErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [submitError, setSubmitError] = useState('')
    const [trap, setTrap] = useState('')
    const pendingFocus = useRef(false)
    const successHeading = useRef<HTMLHeadingElement>(null)

    const currentStep = CONTACT_STEPS[step]
    const isLastStep = step === CONTACT_STEPS.length - 1
    const isSubmitting = status === 'submitting'
    const isSuccess = status === 'success'

    // Move focus after a step change or a finished submit, never on the first render.
    useEffect(() => {
        if (!pendingFocus.current) return

        pendingFocus.current = false

        if (status === 'success') {
            successHeading.current?.focus()
            return
        }

        const fields = CONTACT_STEPS[step].fields
        const target = fields.find(field => errors[field]) ?? fields[0]

        document.getElementById(fieldId(target))?.focus()
    }, [step, status])

    const fieldProps = (field: ContactField) => ({
        id: fieldId(field),
        name: field,
        value: values[field],
        'aria-invalid': Boolean(errors[field]),
        'aria-describedby': errors[field] ? errorId(field) : undefined
    })

    const updateField = (field: ContactField, value: string): void => {
        setValues(previous => ({ ...previous, [field]: value }))

        if (errors[field]) {
            const fieldErrors = validateContact(normalizeContact({ ...values, [field]: value }), [field])

            setErrors(previous => replaceErrors(previous, [field], fieldErrors))
        }

        if (submitError) setSubmitError('')
    }

    const goToStep = (index: number): void => {
        if (isSubmitting || isSuccess || index >= step) return

        setSubmitError('')
        setDirection('back')
        pendingFocus.current = true
        setStep(index)
    }

    const reset = (): void => {
        setValues(EMPTY_CONTACT)
        setErrors({})
        setSubmitError('')
        setTrap('')
        setDirection('none')
        pendingFocus.current = true
        setStep(0)
        setStatus('idle')
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (isSubmitting || isSuccess) return

        const normalized = normalizeContact(values)
        const stepErrors = validateContact(normalized, currentStep.fields)

        setErrors(previous => replaceErrors(previous, currentStep.fields, stepErrors))

        if (hasErrors(stepErrors)) {
            const target = currentStep.fields.find(field => stepErrors[field])

            if (target) document.getElementById(fieldId(target))?.focus()
            return
        }

        if (!isLastStep) {
            setSubmitError('')
            setDirection('forward')
            pendingFocus.current = true
            setStep(step + 1)
            return
        }

        const allErrors = validateContact(normalized)

        if (hasErrors(allErrors)) {
            setErrors(allErrors)
            setDirection('back')
            pendingFocus.current = true
            setStep(Math.max(firstStepWithError(allErrors), 0))
            return
        }

        setStatus('submitting')
        setSubmitError('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...normalized, trap })
            })

            const result = await response.json().catch(() => null) as ContactResponse | null

            if (response.ok && result?.ok) {
                pendingFocus.current = true
                setStatus('success')
                return
            }

            const serverErrors = result && !result.ok ? result.errors ?? {} : {}

            if (hasErrors(serverErrors)) {
                const target = firstStepWithError(serverErrors)

                setErrors(serverErrors)
                pendingFocus.current = true

                if (target >= 0 && target !== step) {
                    setDirection('back')
                    setStep(target)
                }
            }

            setSubmitError(result && !result.ok && result.message ? result.message : FALLBACK_ERROR)
            setStatus('idle')
        }

        catch {
            setSubmitError("We couldn't reach the server. Check your connection and try again.")
            setStatus('idle')
        }
    }

    return (
        <div className='relative w-full max-w-[608px] rounded-lg bg-card p-6 shadow-[0_50px_100px_-20px_rgba(50,50,93,0.14),0_30px_60px_-30px_rgba(0,0,0,0.18)] ring-1 ring-foreground/5 animate-in fade-in slide-in-from-bottom-4 duration-700 ease-out motion-reduce:animate-none sm:p-10'>
            <ContactProgress
                steps={CONTACT_STEPS}
                current={isSuccess ? CONTACT_STEPS.length : step}
                locked={isSubmitting || isSuccess}
                onSelect={goToStep}
            />

            <p className='sr-only' aria-live='polite'>
                {isSuccess
                    ? 'Your message was sent.'
                    : isSubmitting
                        ? 'Sending your message.'
                        : `Step ${step + 1} of ${CONTACT_STEPS.length}: ${currentStep.label}`}
            </p>

            {isSuccess ? (
                <div className='mt-10 animate-in fade-in slide-in-from-bottom-2 duration-500 motion-reduce:animate-none'>
                    <div className='flex size-12 items-center justify-center rounded-full bg-accent text-primary'>
                        <Check className='size-6' strokeWidth={2.5} aria-hidden='true' />
                    </div>

                    <h1
                        id='contact-title'
                        ref={successHeading}
                        tabIndex={-1}
                        className={cn(HEADING_CLASS, 'mt-6 outline-none')}
                    >
                        Thanks, {values.firstName.trim()}. Your message is on its way.
                    </h1>

                    <p className='mt-3 text-[17px] leading-relaxed text-muted-foreground'>
                        We&apos;ll get back to you at <span className='font-medium break-all text-foreground'>{values.email.trim()}</span> as soon as we can.
                    </p>

                    <Button
                        type='button'
                        variant='outline'
                        onClick={reset}
                        className='mt-8 h-11 gap-1.5 rounded-md border-input px-5 text-[15px] text-foreground shadow-[0_1px_2px_rgba(10,37,64,0.05)] hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/30'
                    >
                        Send another message
                        <HoverArrow />
                    </Button>
                </div>
            ) : (
                <form noValidate onSubmit={handleSubmit} className='mt-8'>
                    <div
                        key={step}
                        className={cn(
                            direction === 'forward' && 'animate-in fade-in slide-in-from-right-4 duration-300 ease-out',
                            direction === 'back' && 'animate-in fade-in slide-in-from-left-4 duration-300 ease-out',
                            'motion-reduce:animate-none'
                        )}
                    >
                        <h1 id='contact-title' className={HEADING_CLASS}>
                            {currentStep.title}
                        </h1>

                        <p className='mt-2 text-[17px] leading-relaxed text-muted-foreground'>
                            {currentStep.description}
                        </p>

                        <div className='mt-8 space-y-5'>
                            {step === 0 && (
                                <>
                                    <FieldRow field='email' label='Work email' error={errors.email}>
                                        <Input
                                            {...fieldProps('email')}
                                            type='email'
                                            inputMode='email'
                                            autoComplete='email'
                                            autoCapitalize='none'
                                            spellCheck={false}
                                            placeholder='jane@company.com'
                                            maxLength={CONTACT_LIMITS.email}
                                            required
                                            onChange={event => updateField('email', event.target.value)}
                                            className={CONTROL_CLASS}
                                        />
                                    </FieldRow>

                                    <FieldRow field='company' label='Company' error={errors.company} optional>
                                        <Input
                                            {...fieldProps('company')}
                                            type='text'
                                            autoComplete='organization'
                                            placeholder='Acme Inc.'
                                            maxLength={CONTACT_LIMITS.company}
                                            onChange={event => updateField('company', event.target.value)}
                                            className={CONTROL_CLASS}
                                        />
                                    </FieldRow>
                                </>
                            )}

                            {step === 1 && (
                                <>
                                    <FieldRow field='firstName' label='First name' error={errors.firstName}>
                                        <Input
                                            {...fieldProps('firstName')}
                                            type='text'
                                            autoComplete='given-name'
                                            placeholder='Jane'
                                            maxLength={CONTACT_LIMITS.name}
                                            required
                                            onChange={event => updateField('firstName', event.target.value)}
                                            className={CONTROL_CLASS}
                                        />
                                    </FieldRow>

                                    <FieldRow field='lastName' label='Last name' error={errors.lastName}>
                                        <Input
                                            {...fieldProps('lastName')}
                                            type='text'
                                            autoComplete='family-name'
                                            placeholder='Cooper'
                                            maxLength={CONTACT_LIMITS.name}
                                            required
                                            onChange={event => updateField('lastName', event.target.value)}
                                            className={CONTROL_CLASS}
                                        />
                                    </FieldRow>

                                    <FieldRow field='companySize' label='Company size' error={errors.companySize} optional>
                                        <NativeSelect
                                            {...fieldProps('companySize')}
                                            onChange={event => updateField('companySize', event.target.value)}
                                            className='w-full [&>svg]:right-4'
                                            selectClassName={cn(CONTROL_CLASS, 'pr-11', !values.companySize && 'text-muted-foreground')}
                                        >
                                            <NativeSelectOption value=''>
                                                Select a size
                                            </NativeSelectOption>

                                            {COMPANY_SIZES.map(size => (
                                                <NativeSelectOption key={size} value={size}>
                                                    {size}
                                                </NativeSelectOption>
                                            ))}
                                        </NativeSelect>
                                    </FieldRow>
                                </>
                            )}

                            {step === 2 && (
                                <>
                                    <FieldRow field='topic' label='Topic' error={errors.topic}>
                                        <NativeSelect
                                            {...fieldProps('topic')}
                                            required
                                            onChange={event => updateField('topic', event.target.value)}
                                            className='w-full [&>svg]:right-4'
                                            selectClassName={cn(CONTROL_CLASS, 'pr-11', !values.topic && 'text-muted-foreground')}
                                        >
                                            <NativeSelectOption value='' disabled>
                                                Choose a topic
                                            </NativeSelectOption>

                                            {CONTACT_TOPICS.map(topic => (
                                                <NativeSelectOption key={topic} value={topic}>
                                                    {topic}
                                                </NativeSelectOption>
                                            ))}
                                        </NativeSelect>
                                    </FieldRow>

                                    <FieldRow field='message' label='Message' error={errors.message}>
                                        <Textarea
                                            {...fieldProps('message')}
                                            rows={5}
                                            placeholder='Tell us about your goals, timeline or questions.'
                                            maxLength={CONTACT_LIMITS.messageMax}
                                            required
                                            onChange={event => updateField('message', event.target.value)}
                                            className={cn(CONTROL_CLASS, 'h-auto max-h-72 min-h-36 resize-y py-3 leading-relaxed')}
                                        />
                                    </FieldRow>
                                </>
                            )}
                        </div>
                    </div>

                    <div className='absolute top-0 -left-[10000px] size-px overflow-hidden' aria-hidden='true'>
                        <label htmlFor='contact-trap'>
                            Leave this field empty
                        </label>

                        <input
                            id='contact-trap'
                            name='trap'
                            type='text'
                            tabIndex={-1}
                            autoComplete='off'
                            value={trap}
                            onChange={event => setTrap(event.target.value)}
                        />
                    </div>

                    {isLastStep && (
                        <p className='mt-6 text-[13px] leading-relaxed text-muted-foreground'>
                            We&apos;ll only use your details to reply to your message.
                        </p>
                    )}

                    {submitError && (
                        <div role='alert' className='mt-6 flex items-start gap-2.5 rounded-md border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm leading-snug text-destructive'>
                            <CircleAlert className='mt-px size-4 shrink-0' aria-hidden='true' />
                            <p>
                                {submitError}
                            </p>
                        </div>
                    )}

                    <div className={cn('mt-8 flex items-center gap-4', step > 0 ? 'justify-between' : 'justify-end')}>
                        {step > 0 && (
                            <Button
                                type='button'
                                variant='ghost'
                                onClick={() => goToStep(step - 1)}
                                disabled={isSubmitting}
                                className='-ml-3 h-12 gap-1.5 px-3 text-base text-muted-foreground hover:bg-transparent hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/30'
                            >
                                <HoverArrow direction='left' />
                                Back
                            </Button>
                        )}

                        <Button
                            type='submit'
                            disabled={isSubmitting}
                            className='h-12 gap-1.5 rounded-md px-6 text-base shadow-[0_1px_2px_rgba(10,37,64,0.14)] hover:bg-primary-hover focus-visible:ring-[3px] focus-visible:ring-ring/30 disabled:opacity-80'
                        >
                            {isSubmitting ? (
                                <>
                                    <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />
                                    Sending
                                </>
                            ) : (
                                <>
                                    {isLastStep ? 'Send message' : 'Continue'}
                                    <HoverArrow />
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            )}
        </div>
    )
}
